import { useEffect, type PropsWithChildren } from "react";
import { legacyLogicalPropertiesTransformer, StyleProvider } from "@ant-design/cssinjs";
import { ConfigProvider, theme as antdTheme } from "antd";
import { useTheme } from "../theme-provider";
import { useSelector, useSettingsStore } from "@/stores";

import zhCN from "antd/locale/zh_CN";
import "dayjs/locale/zh-cn";

export function AntdConfigProvider({ children }: PropsWithChildren) {
  const { isDarkMode } = useTheme();
  const { defaultAlgorithm, darkAlgorithm } = antdTheme;
  const { colorPrimary } = useSettingsStore(useSelector(["colorPrimary"]));

  useEffect(() => {
    const token = antdTheme.getDesignToken({
      algorithm: isDarkMode ? darkAlgorithm : defaultAlgorithm,
      token: {
        colorPrimary,
        fontSize: 14,
      },
    });

    const root = document.documentElement;
    const setVar = (key: string, value: string | number | undefined) => {
      if (value === undefined) return;
      root.style.setProperty(key, typeof value === "number" ? `${value}px` : value);
    };

    setVar("--color-primary", token.colorPrimary);
    setVar("--color-primary-hover", token.colorPrimaryHover);
    setVar("--color-primary-active", token.colorPrimaryActive);
    setVar("--color-gradient-start", token.colorPrimary);
    setVar("--color-gradient-end", token.colorPrimaryHover);
    setVar("--color-success", token.colorSuccess);
    setVar("--color-warning", token.colorWarning);
    setVar("--color-error", token.colorError);
    setVar("--color-info", token.colorInfo);

    setVar("--bg-primary", token.colorBgContainer);
    setVar("--bg-secondary", token.colorBgLayout);
    setVar("--bg-tertiary", token.colorFillTertiary);
    setVar("--bg-container", token.colorBgContainer);

    setVar("--text-primary", token.colorText);
    setVar("--text-secondary", token.colorTextSecondary);
    setVar("--text-tertiary", token.colorTextTertiary);
    setVar("--text-disabled", token.colorTextDisabled);
    setVar("--text-light-solid", token.colorTextLightSolid);

    setVar("--border-color", token.colorBorder);
    setVar("--border-light", token.colorBorderSecondary);
    setVar("--border-radius", token.borderRadius);
    setVar("--border-radius-md", token.borderRadiusLG);

    setVar("--shadow-card", token.boxShadow);
    setVar("--shadow-card-hover", token.boxShadowSecondary);

    setVar("--control-outline", token.controlOutline);
    setVar("--fill-secondary", token.colorFillSecondary);
    setVar("--fill-tertiary", token.colorFillTertiary);
    setVar("--fill-quaternary", token.colorFillQuaternary);
  }, [colorPrimary, darkAlgorithm, defaultAlgorithm, isDarkMode]);
  return (
    <StyleProvider hashPriority="low" transformers={[legacyLogicalPropertiesTransformer]}>
      <ConfigProvider
        locale={zhCN}
        theme={{
          cssVar: true, // 开启 css 变量
          hashed: false, // 如果你的应用中只存在一个版本的 antd，你可以设置为 false 来进一步减小样式体积。
          algorithm: isDarkMode ? darkAlgorithm : defaultAlgorithm,
          token: {
            colorPrimary,
            fontSize: 14,
          },
          components: {
            Layout: {
              headerPadding: "0 24px",
              // siderBg: "#141414",
            },
            // Menu: {
            //   darkItemBg: "#141414",
            //   darkSubMenuItemBg: "#141414",
            // },
          },
        }}
        componentSize="middle"
      >
        {children}
      </ConfigProvider>
    </StyleProvider>
  );
}
